import React from 'react';

/**
 * BackfillModal — shown when the user triggers a location backfill.
 *
 * Props:
 *   phase    — 'confirm' | 'running' | 'done'
 *   result   — { updated, failed, total } or { error } once phase === 'done'
 *   onConfirm — called when user clicks OK on the confirm prompt
 *   onClose   — called when user dismisses the modal (Cancel or Done)
 */
function BackfillModal({ phase, result, onConfirm, onClose }) {
  return (
    <div style={styles.overlay}>
      <div style={styles.dialog} role="dialog" aria-modal="true">
        {phase === 'confirm' && (
          <>
            <h3 style={styles.title}>Backfill Locations</h3>
            <p style={styles.body}>
              Attempt location backfill based on callsign?
            </p>
            <p style={styles.hint}>
              Each log entry without location data will be looked up by callsign.
              Existing location data will not be overwritten.
            </p>
            <div style={styles.actions}>
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="btn" onClick={onConfirm}>
                OK
              </button>
            </div>
          </>
        )}

        {phase === 'running' && (
          <>
            <h3 style={styles.title}>Backfilling Locations…</h3>
            <div style={styles.spinnerWrap}>
              <div style={styles.spinner} />
            </div>
            <p style={{ ...styles.body, marginTop: '1rem' }}>
              Looking up callsigns — this may take a moment.
            </p>
          </>
        )}

        {phase === 'done' && (
          <>
            <h3 style={styles.title}>Backfill Complete</h3>
            {result?.error ? (
              <p style={{ ...styles.body, color: 'var(--error, #e74c3c)' }}>
                {result.error}
              </p>
            ) : (
              <div style={styles.results}>
                <ResultRow label="Contacts checked" value={result?.total ?? 0} />
                <ResultRow label="Locations filled" value={result?.updated ?? 0} highlight="success" />
                {result?.failed > 0 && (
                  <ResultRow label="Not found / skipped" value={result.failed} highlight="warn" />
                )}
              </div>
            )}
            <div style={styles.actions}>
              <button className="btn" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes aurora-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function ResultRow({ label, value, highlight }) {
  const color =
    highlight === 'success' ? 'var(--success, #27ae60)' :
    highlight === 'warn'    ? 'var(--warning, #e67e22)' :
    'var(--text-primary, inherit)';

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', borderBottom: '1px solid var(--border, #333)' }}>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontWeight: 600, color }}>{value}</span>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  dialog: {
    background: 'var(--bg-card, #1e1e1e)',
    border: '1px solid var(--border, #333)',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '320px',
    maxWidth: '440px',
    width: '90%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  title: {
    margin: '0 0 1rem',
    fontSize: '1.1rem',
    color: 'var(--text-primary)',
  },
  body: {
    margin: '0 0 0.5rem',
    color: 'var(--text-primary)',
    lineHeight: 1.5,
  },
  hint: {
    margin: '0 0 1.25rem',
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1.25rem',
  },
  spinnerWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '0.5rem',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid var(--border, #333)',
    borderTop: '4px solid var(--accent, #4a9eff)',
    borderRadius: '50%',
    animation: 'aurora-spin 0.8s linear infinite',
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.1rem',
    margin: '0.5rem 0 0',
  },
};

export default BackfillModal;
